import React, { useState } from 'react';
import { FileCode2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useSourceExportEntitlement } from '@/hooks/useSourceExportEntitlement';
import { downloadWriterSource, SourceExportDenied } from '@/lib/writer/exportSource';

interface Props {
  versionId: string | null | undefined;
  /** `link` renders an inline text action, `menu` a full-width row label. */
  variant?: 'link' | 'menu';
  className?: string;
  label?: string;
}

/**
 * Downloads the raw LaTeX source (main.tex + references.bib + README) of a
 * Writer version. Available to admins and paid plans; free users see the
 * control disabled with an upgrade pointer instead of it being hidden.
 *
 * Server-side the `writer_export_source` RPC enforces the same rule, so this
 * gate is product surface, not the security boundary.
 */
const WriterSourceExportButton: React.FC<Props> = ({
  versionId,
  variant = 'link',
  className,
  label = 'Download source',
}) => {
  const { allowed, reason, loading } = useSourceExportEntitlement();
  const [busy, setBusy] = useState(false);

  if (!versionId) return null;

  const disabled = busy || loading || !allowed;

  const handleClick = async () => {
    if (!allowed || busy) return;
    setBusy(true);
    try {
      await downloadWriterSource(versionId);
      toast.success('LaTeX source downloaded');
    } catch (err) {
      if (err instanceof SourceExportDenied && err.kind === 'upgrade_required') {
        toast.error('Source export is included in paid plans');
      } else if (err instanceof SourceExportDenied && err.kind === 'not_found') {
        toast.error('This version is no longer available');
      } else if (err instanceof SourceExportDenied && err.kind === 'unavailable') {
        toast.error('Source export is not available yet — please retry in a moment');
      } else {
        toast.error('Could not export the LaTeX source');
      }

    } finally {
      setBusy(false);
    }
  };

  const button = (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      className={cn(
        'inline-flex items-center gap-1.5 text-xs text-muted-foreground underline-offset-4',
        !disabled && 'hover:underline hover:text-foreground',
        disabled && 'opacity-60 cursor-not-allowed',
        variant === 'menu' && 'w-full justify-start text-sm',
        className,
      )}
    >
      {busy ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <FileCode2 className="h-3.5 w-3.5" />
      )}
      <span>{label}</span>
    </button>
  );

  const tip = allowed
    ? 'Download main.tex, references.bib and a README — no credits used.'
    : reason === 'anonymous'
      ? 'Sign in to export the LaTeX source.'
      : 'Source export is included in paid plans.';

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={variant === 'menu' ? 'w-full' : undefined}>{button}</span>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs text-xs">
          <p>{tip}</p>
          {!allowed && reason === 'free' && (
            <Link to="/pricing" className="mt-1 block underline">
              See plans
            </Link>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default WriterSourceExportButton;
